import { Button, Text } from '@tunein/react-ui';
import clsx from 'clsx';
import { FORM_ERROR } from 'final-form';
import { useContext } from 'react';
import { Form } from 'react-final-form';
import { clearAllRecents } from 'src/common/actions/browse';
import {
  CLEAR_ALL_RECENTS_BUTTON,
  CLEAR_ALL_RECENTS_DIALOG_DESCRIPTION,
  CLEAR_ALL_RECENTS_DIALOG_TITLE,
  CLEAR_RECENTS_ERROR,
} from 'src/common/constants/localizations/settings';
import { CANCEL } from 'src/common/constants/localizations/shared';
import useActions from 'src/common/hooks/useActions';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import commonSettingsCss from '../settings/account-settings.module.scss';
import css from '../settings/general/general-settings.module.scss';

interface ClearAllRecentsFormProps {
  handleClose: () => void;
}

interface ClearAllRecentsFormSubmitError {
  [FORM_ERROR]: string;
}

export function ClearAllRecentsForm({ handleClose }: ClearAllRecentsFormProps) {
  const { getLocalizedText } = useContext(LocationAndLocalizationContext);
  const actions = useActions<{ clearAllRecents: () => Promise<unknown> }>({ clearAllRecents });

  async function onSubmit(): Promise<void | ClearAllRecentsFormSubmitError> {
    try {
      await actions.clearAllRecents();
      handleClose();
    } catch {
      return {
        [FORM_ERROR]: getLocalizedText(CLEAR_RECENTS_ERROR),
      };
    }
  }

  return (
    <Form onSubmit={onSubmit}>
      {({ handleSubmit, submitting, submitError }) => (
        <form name="clearAllRecents" data-testid="clearAllRecentsForm" onSubmit={handleSubmit}>
          <Text as="h2" data-testid="headline" className={commonSettingsCss.contentTitle}>
            {getLocalizedText(CLEAR_ALL_RECENTS_DIALOG_TITLE)}
          </Text>
          <Text
            data-testid="bodyText"
            className={clsx(commonSettingsCss.contentBodyText, commonSettingsCss.withMargin)}
          >
            {getLocalizedText(CLEAR_ALL_RECENTS_DIALOG_DESCRIPTION)}
          </Text>
          {submitError && (
            <Text data-testid="mainError" variant="danger" className={commonSettingsCss.errorField}>
              {submitError}
            </Text>
          )}
          <div className="row">
            <div className="col-xs-12">
              <Button
                data-testid="clearAllRecentsConfirmButton"
                type="submit"
                className={clsx(css.dialogButton, css.dialogPrimaryButton)}
                disabled={submitting}
                radius="full"
                variant="primary"
              >
                {getLocalizedText(CLEAR_ALL_RECENTS_BUTTON)}
              </Button>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12">
              <Button
                data-testid="clearAllRecentsCancelButton"
                className={css.dialogButton}
                onClick={handleClose}
                disabled={submitting}
                radius="full"
                type="button"
                variant="outlined"
              >
                {getLocalizedText(CANCEL)}
              </Button>
            </div>
          </div>
        </form>
      )}
    </Form>
  );
}
